/**
 * iter315 — Fil d'une conversation WhatsApp : échanges, brouillons de Claude,
 * vérification d'identité, reprise humaine et envoi manuel.
 * L'envoi n'est proposé que si le serveur le permet ; sinon on affiche pourquoi.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, UserCheck, Send, Bot, User, Lock, Paperclip, FileText, Image as ImageIcon, Mic, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import RattacherClientDialog from "@/components/whatsapp/RattacherClientDialog";

const AUTEURS = {
  client: { Icon: User, libelle: "Client", classe: "bg-white border-gray-200" },
  claude: { Icon: Bot, libelle: "Claude", classe: "bg-[#f0f9f4] border-[#52a675]/30" },
  collaborateur: { Icon: UserCheck, libelle: "Garage", classe: "bg-blue-50 border-blue-200" },
  systeme: { Icon: Lock, libelle: "Système", classe: "bg-gray-50 border-gray-200" },
};

const ICONES_MEDIA = { image: ImageIcon, sticker: ImageIcon, document: FileText, audio: Mic, video: Film };

// iter347 — Pièce jointe d'un message : vignette cliquable pour les photos,
// pastille téléchargeable pour les documents, audios et vidéos.
const MediaPieceJointe = ({ message }) => {
  const media = message.meta?.media || {};
  const [busy, setBusy] = useState(false);
  const Icone = ICONES_MEDIA[media.type] || Paperclip;
  const taille = media.taille ? `${Math.max(1, Math.round(media.taille / 1024))} Ko` : null;

  const ouvrir = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/whatsapp/messages/${message.id}/media`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Pièce jointe illisible");
    } finally {
      setBusy(false);
    }
  };

  if (!media.stocke) {
    return (
      <div className="mt-1.5 text-xs text-amber-700 flex items-center gap-1.5" data-testid={`whatsapp-media-absent-${message.id}`}>
        <Paperclip className="w-3.5 h-3.5" />
        Pièce jointe non récupérée{media.motif ? ` — ${media.motif}` : ""}
      </div>
    );
  }

  return (
    <div className="mt-1.5" data-testid={`whatsapp-media-${message.id}`}>
      {media.vignette ? (
        <button type="button" onClick={ouvrir} className="block group" title="Ouvrir en grand">
          <img
            src={media.vignette}
            alt={media.filename || "Photo reçue"}
            className="rounded-md border border-gray-200 max-h-56 object-contain bg-gray-50 group-hover:opacity-90 transition-opacity"
            data-testid={`whatsapp-media-vignette-${message.id}`}
          />
        </button>
      ) : (
        <button
          type="button" onClick={ouvrir} disabled={busy}
          className="flex items-center gap-2 text-xs bg-white border border-gray-200 rounded px-2.5 py-1.5 hover:bg-gray-50 max-w-full"
          data-testid={`whatsapp-media-fichier-${message.id}`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icone className="w-4 h-4 text-gray-500" />}
          <span className="truncate max-w-[220px]">{media.filename || media.type}</span>
          {taille && <span className="text-gray-400 flex-shrink-0">{taille}</span>}
        </button>
      )}
    </div>
  );
};

export const WhatsAppConversation = ({ waId, onChange }) => {
  const [data, setData] = useState(null);
  const [texte, setTexte] = useState("");
  const [busy, setBusy] = useState(false);
  const [rattacherOpen, setRattacherOpen] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/whatsapp/conversations/${waId}`);
      setData(d);
    } catch {
      toast.error("Conversation introuvable");
    }
  }, [waId]);

  useEffect(() => { charger(); }, [charger]);

  const action = async (chemin, corps, succes) => {
    setBusy(true);
    try {
      await api.post(`/whatsapp/conversations/${waId}/${chemin}`, corps);
      toast.success(succes);
      await charger();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally {
      setBusy(false);
    }
  };

  const envoyer = async () => {
    if (!texte.trim()) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/whatsapp/conversations/${waId}/envoyer`, { texte });
      if (res.envoye) toast.success("Message envoyé");
      else toast.warning("Message non envoyé", { description: res.motif });
      setTexte("");
      await charger();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <Card className="border-gray-200 p-10 text-center text-sm text-gray-400">Chargement…</Card>;
  }
  const { conversation: conv, messages, envoi } = data;

  return (
    <Card className="border-gray-200 flex flex-col" data-testid="whatsapp-thread">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-900">
          {conv.client_label || conv.nom_contact || `+${conv.wa_id}`}
        </span>
        {conv.identite_verifiee ? (
          <Badge className="bg-[#52a675] hover:bg-[#52a675] text-white gap-1">
            <ShieldCheck className="w-3 h-3" /> Dossier accessible
          </Badge>
        ) : (
          <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1">
            <ShieldAlert className="w-3 h-3" /> Aucun dossier rattaché
          </Badge>
        )}
        {conv.reprise_humaine && <Badge variant="outline" className="text-blue-700 border-blue-300">Repris par un collaborateur</Badge>}
        <div className="flex-1" />
        <Button
          size="sm" variant="outline" disabled={busy}
          onClick={() => setRattacherOpen(true)}
          data-testid="whatsapp-rattacher"
        >
          <UserCheck className="w-3.5 h-3.5 mr-1.5" />
          {conv.client_id ? "Changer de client" : "Rattacher un client"}
        </Button>
        <Button
          size="sm" variant="outline" disabled={busy}
          onClick={() => action("identite", { verifiee: !conv.identite_verifiee },
            conv.identite_verifiee ? "Vérification retirée" : "Identité confirmée")}
          data-testid="whatsapp-identite"
        >
          {conv.identite_verifiee ? "Retirer la vérification" : "Confirmer l'identité"}
        </Button>
        <Button
          size="sm" variant="outline" disabled={busy}
          onClick={() => action("reprise", { actif: !conv.reprise_humaine },
            conv.reprise_humaine ? "Claude peut de nouveau répondre" : "Vous avez repris la main")}
          data-testid="whatsapp-reprise"
        >
          {conv.reprise_humaine ? "Rendre la main à Claude" : "Reprendre la conversation"}
        </Button>
      </div>

      <RattacherClientDialog
        open={rattacherOpen}
        waId={waId}
        actuelLabel={conv.client_label}
        onClose={() => setRattacherOpen(false)}
        onDone={async () => { await charger(); onChange?.(); }}
      />

      {!conv.client_id && (
        <p className="px-4 py-2 text-xs bg-amber-50 text-amber-800 border-b border-amber-200">
          Ce numéro n'est rattaché à aucun client unique du CRM : Claude peut renseigner et
          prendre un rendez-vous, mais n'a aucun dossier à consulter.
        </p>
      )}

      <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto" data-testid="whatsapp-messages">
        {messages.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucun message</p>}
        {messages.map((m) => {
          const a = AUTEURS[m.auteur] || AUTEURS.systeme;
          const sortant = m.direction !== "entrant";
          return (
            <div key={m.id} className={`flex ${sortant ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg border px-3 py-2 ${a.classe}`} data-testid={`whatsapp-msg-${m.id}`}>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                  <a.Icon className="w-3 h-3" /> {a.libelle}
                  {m.direction === "simule" && (
                    <span className="text-amber-700">· brouillon, non envoyé</span>
                  )}
                  {m.statut && m.direction === "sortant" && <span>· {m.statut}</span>}
                </div>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{m.texte}</p>
                {/* iter347 — Pièce jointe visible (photo, document, audio, vidéo) */}
                {m.meta?.media && <MediaPieceJointe message={m} />}
                {!m.texte && !m.meta?.media && (
                  <p className="text-sm text-gray-500 italic">[{m.type_message}]</p>
                )}
                {m.meta?.motif && <p className="text-[11px] text-amber-700 mt-1">{m.meta.motif}</p>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-3 border-t bg-gray-50">
        {!envoi.autorise && (
          <p className="text-xs text-gray-600 mb-2 flex items-center gap-1.5" data-testid="whatsapp-envoi-bloque">
            <Lock className="w-3.5 h-3.5" /> {envoi.motif}
          </p>
        )}
        <div className="flex gap-2">
          <Textarea
            rows={2}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder="Écrire au client…"
            className="bg-white"
            data-testid="whatsapp-input"
          />
          <Button
            className="bg-[#52a675] hover:bg-[#3d8a5c] self-end"
            disabled={busy || !texte.trim()}
            onClick={envoyer}
            data-testid="whatsapp-envoyer"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default WhatsAppConversation;
